/**
 * Analytics — two action plans, not a wall of charts.
 *
 * The same data used to be a paper picker, a class stats bar, a marks matrix,
 * a grid of question cards, five bucket tiles, a drill-down and a per-student
 * breakdown on one route — seven ways of saying one thing, none of which is
 * the sheet the school already prints and works from. Here it is two reports
 * of one paper, with a toggle between them:
 *
 *   • WHOLE CLASS — where does this class need me, and who for;
 *   • ONE STUDENT — that child's own report, the copy that goes home.
 *
 * A paper is read one CLASS at a time, because a paper is set for a class and
 * read by that class's teachers of its subject: each card in the picker is a
 * (paper × class), and a board paper two of your classes sat is two cards.
 *
 * `?paperId=` preselects a paper — `/teacher/papers/:paperId` lands here.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api';
import { BIN_LABEL, BIN_ORDER, binRamp } from '@/lib/bins';
import type {
  ClassPlanOption,
  HomeworkDraft,
  ReadingScope,
  StudentPlanOption,
} from '@cwy/config/contracts';
import { ClassActionPlan } from '@/components/teacher/ClassActionPlan';
import { StudentProgress } from '@/components/reports/StudentProgress';
import { BandRamp, MODE_LABEL } from '@/components/reports/ActionPlanKit';
import { HomeworkPanel } from '@/components/teacher/HomeworkPanel';
import { PeerPairs } from '@/components/teacher/PeerPairs';
import { RepeatStrugglers } from '@/components/teacher/RepeatStrugglers';
import { useTeacherYear, withYear } from '@/hooks/useTeacherYear';

type View = 'class' | 'student';

const optionKey = (o: { paperId: string; classroomId: string | null }) =>
  `${o.paperId}:${o.classroomId ?? ''}`;

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** The toggle between the two plans. Two views, so two buttons, not a dropdown. */
function ViewToggle({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  return (
    <div
      className="inline-flex rounded-full p-0.5"
      style={{ background: 'var(--paper-sunk)', border: '1px solid var(--border-default)' }}
      role="tablist"
      aria-label="Which plan to read"
    >
      {(
        [
          ['class', 'Whole class'],
          ['student', 'One student'],
        ] as const
      ).map(([key, label]) => {
        const active = view === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(key)}
            className="rounded-full px-3.5 py-1.5 text-xs font-bold transition"
            style={{
              background: active ? 'var(--slab)' : 'transparent',
              color: active ? 'var(--text-on-forest)' : 'var(--ink-2)',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** Say the scope out loud: a teacher who cannot find Science needs to know why. */
function scopeLine(scope: ReadingScope): string {
  if (scope.readsEverySubject) return 'Reading every class and subject in your school';
  if (scope.classrooms.length === 0) return 'You are not assigned to a class yet';
  return scope.classrooms
    .map((room) => `${room.name}: ${room.subjectNames.join(', ') || 'no subject'}`)
    .join(' · ');
}

export function TeacherAnalyticsPage() {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<View>('class');
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  const [roomFilter, setRoomFilter] = useState<string | null>(null);
  const [pickedStudent, setPickedStudent] = useState<string | null>(null);
  const [previews, setPreviews] = useState<HomeworkDraft[] | null>(null);
  const linkedPaperId = searchParams.get('paperId');
  // One academic year, the header's — last year's classes read the same way,
  // they simply cannot be set new work.
  const { yearParam } = useTeacherYear();

  const scopeQuery = useQuery({
    queryKey: ['reading-scope', yearParam],
    queryFn: () => apiGet<ReadingScope>(withYear('/teacher/reading-scope', yearParam)),
  });
  const scope = scopeQuery.data;

  const papersQuery = useQuery({
    queryKey: ['class-plans', yearParam],
    queryFn: () => apiGet<ClassPlanOption[]>(withYear('/teacher/class-plans', yearParam)),
  });

  const rooms = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of papersQuery.data ?? []) {
      if (p.classroomId) seen.set(p.classroomId, p.classroomName);
    }
    return [...seen].map(([id, name]) => ({ id, name }));
  }, [papersQuery.data]);

  const papers = useMemo(
    () => (papersQuery.data ?? []).filter((p) => !roomFilter || p.classroomId === roomFilter),
    [papersQuery.data, roomFilter],
  );
  const selected =
    papers.find((p) => optionKey(p) === pickedKey) ??
    (linkedPaperId ? papers.find((p) => p.paperId === linkedPaperId) : undefined) ??
    papers[0];
  const paperId = selected?.paperId;
  const classroomId = selected?.classroomId ?? undefined;
  const selectedKey = selected ? optionKey(selected) : null;

  // Loaded for both views: the student strip needs it, and so does the
  // homework panel on the class view, which is handed student ids and nothing
  // else.
  const studentsQuery = useQuery({
    queryKey: ['student-plans', paperId, classroomId],
    enabled: Boolean(paperId && classroomId),
    queryFn: () =>
      apiGet<StudentPlanOption[]>(
        `/teacher/papers/${paperId}/student-plans?classroomId=${encodeURIComponent(classroomId!)}`,
      ),
  });
  const students = useMemo(() => studentsQuery.data ?? [], [studentsQuery.data]);
  const student = students.find((s) => s.studentId === pickedStudent) ?? students[0];

  const nameById = useMemo(
    () => new Map(students.map((s) => [s.studentId, s.studentName])),
    [students],
  );

  const studentViewRef = useRef<HTMLDivElement>(null);
  const activeCardRef = useRef<HTMLButtonElement>(null);
  /** Set only by a name click, so using the tabs never yanks the page about. */
  const scrollToStudent = useRef(false);

  /** Every name on this page opens that child's own report. */
  const openStudent = (studentId: string) => {
    setPickedStudent(studentId);
    setView('student');
    scrollToStudent.current = true;
  };

  // The names being clicked sit a long way down — the re-teach column, the
  // strugglers panel, the pair list. Switching the tab at the top without
  // moving left the teacher looking at whatever had replaced what they clicked.
  useEffect(() => {
    if (view !== 'student' || !scrollToStudent.current) return;
    scrollToStudent.current = false;
    studentViewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    activeCardRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [view, pickedStudent]);

  const homework = useMutation({
    mutationFn: () =>
      apiPost<HomeworkDraft[]>('/teacher/homework/generate', { paperId, classroomId }),
    onSuccess: (data) => setPreviews(data),
  });

  // A student chosen on one paper is meaningless on the next, and silently
  // keeping the id shows the wrong child's name over the right paper's marks.
  // Homework is per paper for the same reason.
  useEffect(() => {
    setPickedStudent(null);
    setPreviews(null);
  }, [selectedKey]);

  return (
    <div className="flex w-full flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold leading-tight text-forest">Action plans</h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--ink-2)' }}>
            Assessment for learning — one paper, read two ways: where the class needs you, and what
            goes home with one child.
          </p>
          {scope && (
            <p className="mt-1.5 text-xs font-semibold" style={{ color: 'var(--ink-3)' }}>
              {scopeLine(scope)}
            </p>
          )}
        </div>
        <div className="flex flex-none items-center gap-2">
          <ViewToggle view={view} onChange={setView} />
          {paperId && (
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-full px-3.5 py-2 text-xs font-bold transition"
              style={{ background: 'var(--paper-sunk)', color: 'var(--ink-2)' }}
            >
              Print plan
            </button>
          )}
        </div>
      </header>

      {papersQuery.isLoading ? (
        <div
          className="h-24 animate-pulse rounded-2xl"
          style={{ background: 'var(--surface-sunk)' }}
        />
      ) : papersQuery.isError ? (
        <p className="p-6 text-center" style={{ color: 'var(--ink-3)' }}>
          Couldn't load your papers.
        </p>
      ) : (papersQuery.data ?? []).length === 0 ? (
        <div
          className="rounded-2xl border border-dashed p-10 text-center"
          style={{ borderColor: 'var(--border-strong)', background: 'var(--surface-card)' }}
        >
          <p className="font-bold" style={{ color: 'var(--text-body)' }}>
            No papers yet
          </p>
          <p className="mt-1 text-sm" style={{ color: 'var(--ink-3)' }}>
            Publish a paper for one of your classes and its plan appears here.
          </p>
        </div>
      ) : (
        <>
          {rooms.length > 1 && (
            <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Which class">
              {[{ id: null, name: 'All classes' }, ...rooms].map((room) => {
                const active = roomFilter === room.id;
                return (
                  <button
                    key={room.id ?? 'all'}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setRoomFilter(room.id)}
                    className="rounded-full px-3 py-1 text-xs font-bold transition"
                    style={
                      active
                        ? { background: 'var(--slab)', color: 'var(--text-on-forest)' }
                        : { background: 'var(--paper-sunk)', color: 'var(--ink-2)' }
                    }
                  >
                    {room.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Which paper ── */}
          <section aria-label="Choose a paper">
            <h2 className="mb-2 text-sm font-bold" style={{ color: 'var(--text-body)' }}>
              Papers your classes have sat
            </h2>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {papers.map((p) => {
                const active = optionKey(p) === selectedKey;
                return (
                  <button
                    key={optionKey(p)}
                    type="button"
                    onClick={() => setPickedKey(optionKey(p))}
                    aria-pressed={active}
                    className="flex w-48 flex-none flex-col gap-1.5 rounded-xl px-3 py-2.5 text-left transition"
                    style={{
                      background: active ? 'var(--brand-lime-soft)' : 'var(--surface-card)',
                      border: `1px solid ${active ? 'var(--brand-ink)' : 'var(--border-default)'}`,
                      boxShadow: active ? 'var(--shadow-sm)' : undefined,
                    }}
                  >
                    <span className="flex items-baseline justify-between gap-2">
                      <span
                        className="truncate text-fs-11 font-bold uppercase tracking-wide"
                        style={{ color: 'var(--ink-3)' }}
                      >
                        {p.classroomName} · {p.subject}
                      </span>
                      <span
                        data-numeric=""
                        className="flex-none text-sm font-extrabold"
                        style={{ color: 'var(--text-strong)' }}
                      >
                        {p.classAveragePercent}%
                      </span>
                    </span>

                    <span
                      className="line-clamp-2 text-fs-12 font-semibold leading-snug"
                      style={{ color: 'var(--text-body)' }}
                    >
                      {p.title}
                    </span>

                    <BandRamp counts={p.binCounts} height={6} />

                    <span
                      data-numeric=""
                      className="text-fs-10 font-semibold"
                      style={{ color: 'var(--ink-3)' }}
                    >
                      {MODE_LABEL[p.mode] ?? p.mode} · {fmtDate(p.date)} · {p.submitted}/
                      {p.totalStudents} in
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* ── Which child, once the plan is the student one ── */}
          {view === 'student' && paperId && (
            <section aria-label="Choose a student" ref={studentViewRef}>
              <h2 className="mb-2 text-sm font-bold" style={{ color: 'var(--text-body)' }}>
                The class, weakest first
              </h2>
              {studentsQuery.isLoading ? (
                <div
                  className="h-16 animate-pulse rounded-xl"
                  style={{ background: 'var(--surface-sunk)' }}
                />
              ) : (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {students.map((s) => {
                    const active = s.studentId === student?.studentId;
                    const worst = BIN_ORDER.filter((b) => s.binCounts[b] > 0).at(-1) ?? 'GREEN';
                    return (
                      <button
                        key={s.studentId}
                        type="button"
                        // The strip is weakest-first and scrolls sideways, so a
                        // child opened by name from the sheet is usually off
                        // the right-hand edge without this.
                        ref={active ? activeCardRef : undefined}
                        onClick={() => setPickedStudent(s.studentId)}
                        aria-pressed={active}
                        title={BIN_ORDER.map((b) => `${BIN_LABEL[b]} ${s.binCounts[b]}`).join(', ')}
                        className="flex w-40 flex-none flex-col gap-1.5 rounded-xl px-3 py-2 text-left transition"
                        style={{
                          background: active ? 'var(--brand-lime-soft)' : 'var(--surface-card)',
                          border: `1px solid ${active ? 'var(--brand-ink)' : 'var(--border-default)'}`,
                        }}
                      >
                        <span className="flex items-baseline justify-between gap-2">
                          <span
                            className="truncate text-fs-12 font-bold"
                            style={{ color: 'var(--text-strong)' }}
                          >
                            {s.studentName}
                          </span>
                          <span
                            data-numeric=""
                            className="flex-none text-fs-12 font-extrabold"
                            style={{
                              color: s.submitted === null ? 'var(--ink-3)' : binRamp(worst).text,
                            }}
                          >
                            {s.submitted === null ? '—' : `${s.percent}%`}
                          </span>
                        </span>
                        <BandRamp counts={s.binCounts} height={5} />
                        <span
                          data-numeric=""
                          className="text-fs-10 font-semibold"
                          style={{ color: 'var(--ink-3)' }}
                        >
                          Roll {s.rollNumber}
                          {s.submitted === null
                            ? ' · not started'
                            : s.submitted
                              ? ''
                              : ' · mid-paper'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {paperId && classroomId && view === 'class' && (
            <>
              <ClassActionPlan
                paperId={paperId}
                classroomId={classroomId}
                onOpenStudent={openStudent}
              />

              {/* What the teacher DOES with the sheet above. Screen-only — the
                  print rules show the sheet and nothing else: these are
                  working tools, not part of the report a school files. */}
              <section aria-label="Plan the follow-up" className="flex flex-col gap-3 print:hidden">
                <div>
                  <h2 className="text-sm font-bold" style={{ color: 'var(--text-body)' }}>
                    Plan the follow-up
                  </h2>
                  <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
                    Reflective practice — what the sheet above changes about next week: who is
                    stuck, who can teach whom, and what goes home tonight.
                  </p>
                </div>

                <RepeatStrugglers classroomId={classroomId} onOpenStudent={openStudent} />

                <PeerPairs
                  paperId={paperId}
                  classroomId={classroomId}
                  onOpenStudent={openStudent}
                />

                {previews ? (
                  <HomeworkPanel
                    key={selectedKey ?? ''}
                    previews={previews}
                    nameFor={(id) => nameById.get(id) ?? 'Unknown student'}
                  />
                ) : (
                  <div
                    className="flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4"
                    style={{
                      background: 'var(--surface-card)',
                      border: '1px solid var(--border-default)',
                    }}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-bold" style={{ color: 'var(--text-body)' }}>
                        Bucket homework
                      </p>
                      <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
                        One list per student, built from the band each question landed in.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => homework.mutate()}
                      disabled={homework.isPending}
                      className="flex-none rounded-full px-4 py-2 text-xs font-bold text-white transition disabled:opacity-40"
                      style={{ background: 'var(--brand-forest)' }}
                    >
                      {homework.isPending ? 'Building…' : 'Generate homework'}
                    </button>
                  </div>
                )}

                {homework.isError && (
                  <p className="text-sm" style={{ color: 'var(--danger)' }}>
                    Couldn't build the homework for this paper.
                  </p>
                )}
              </section>
            </>
          )}

          {paperId && view === 'student' && student && (
            <Link
              to={`/teacher/ptm/${student.studentId}`}
              className="flex flex-wrap items-center justify-between gap-2 rounded-2xl px-4 py-3 no-underline print:hidden"
              style={{ background: 'var(--paper-sunk)', border: '1px solid var(--border-default)' }}
            >
              <span className="text-sm font-bold" style={{ color: 'var(--text-body)' }}>
                {student.studentName}’s term report
                <span className="ml-2 text-xs font-normal" style={{ color: 'var(--ink-3)' }}>
                  every paper this term, pressure points, for the parents’ evening
                </span>
              </span>
              <span className="text-xs font-bold" style={{ color: 'var(--brand-ink)' }}>
                Open →
              </span>
            </Link>
          )}
          {paperId && view === 'student' && student && (
            <StudentProgress
              // The child's whole progress, not just this paper: the sheet a
              // teacher discusses at a PTM is the one the child is reading. The
              // server sends only the subjects this teacher may open.
              key={student.studentId}
              studentId={student.studentId}
              possessive={`${student.studentName.split(' ')[0]}’s`}
              viewer="teacher"
              emptyNote="Nothing submitted yet."
            />
          )}
        </>
      )}
    </div>
  );
}
